import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import BeforeStateCarousel from "../../components/PrimateVision/BeforeStateCarousel";
import { useIsMobile } from "../../hooks/useIsMobile";

const PASSWORD = import.meta.env.VITE_DEV_PASSWORD ?? "punchthemonkey";
const STORAGE_KEY = "primate-dev-auth";
// VITE_PASSWORD_GATE_ENABLED accepts three values:
//   'true'         → gate always on (default — dev/staging)
//   'false'        → gate fully off (no longer used on prod; waitlist was the gate)
//   'mobile-only'  → gate on for mobile viewports only (prod, until mobile UX ships)
// Resolved at render time (not module load) so `vi.stubEnv` in tests works.
// Default = enabled. Only the literal strings 'false' or 'mobile-only' change
// behaviour; typos like '0' / 'no' keep the gate fully on (safe direction).
type GateMode = 'on' | 'off' | 'mobile-only'
function gateMode(): GateMode {
  const raw = import.meta.env.VITE_PASSWORD_GATE_ENABLED
  if (raw === 'false') return 'off'
  if (raw === 'mobile-only') return 'mobile-only'
  return 'on'
}

// ── Light-mode palette ────────────────────────────────────────────────────────
const L = {
  bgPage:      '#F2F0EB',   // warm linen — matches v2 page
  bgSurface:   '#FAFAF7',
  border:      '#D4D0C8',
  textPrimary: '#1A1916',
  textMuted:   '#7A7669',
  signal:      '#1B4FE0',
  signalHover: '#1340C8',
};

export default function PasswordGate({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const isMobile = useIsMobile();

  // Bypass gate for invite / auth flows so invited users don't need the dev password.
  // /design-partner: external partners follow an emailed click-to-sign link and
  // have no dev password (Matt hit this as a blocked flow on dev, 2026-08-13).
  const bypassRoutes = ['/invite', '/signup', '/login', '/design-partner'];
  const isBypassed = bypassRoutes.some(r => pathname.startsWith(r));

  // Derive from localStorage each render — component re-renders on route change
  // via useLocation, so this always reflects the current auth state without a
  // race between React state and the router context update.
  const unlocked = (() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "1" || stored === "punchthemonkey";
  })();

  const navigate = useNavigate();
  const [input, setInput]   = useState("");
  const [shake, setShake]   = useState(false);
  const [focused, setFocused] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input === PASSWORD) {
      localStorage.setItem(STORAGE_KEY, "1");
      // navigate triggers a location change → re-render → unlocked derived as
      // true → the product renders. PRI-503: the waitlist gate is gone — land
      // unlocked users directly in the product at / (anon free trial).
      navigate('/', { replace: true });
    } else {
      setShake(true);
      setInput("");
      setTimeout(() => setShake(false), 600);
    }
  };

  // Skip the gate entirely on environments where it's disabled, or when the
  // mode is 'mobile-only' and we're on a desktop viewport. Bypassed routes
  // (invite/signup/login) and already-unlocked sessions also skip.
  const mode = gateMode();
  if (mode === 'off' || unlocked || isBypassed) return <>{children}</>;
  if (mode === 'mobile-only' && !isMobile) return <>{children}</>;

  return (
    <>
      <style>{`
        @keyframes pg-shake {
          0%,100% { transform: translateX(0); }
          18%     { transform: translateX(-9px); }
          36%     { transform: translateX(9px); }
          54%     { transform: translateX(-6px); }
          72%     { transform: translateX(6px); }
        }
        .pg-shake { animation: pg-shake 0.55s cubic-bezier(0.36,0.07,0.19,0.97) both; }

        @keyframes pg-fade-up {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .pg-item-1 { animation: pg-fade-up 0.55s cubic-bezier(0.16,1,0.3,1) 0.00s both; }
        .pg-item-2 { animation: pg-fade-up 0.55s cubic-bezier(0.16,1,0.3,1) 0.10s both; }
        .pg-item-3 { animation: pg-fade-up 0.55s cubic-bezier(0.16,1,0.3,1) 0.20s both; }

        .pg-enter-btn:hover  { background: ${L.signalHover} !important; }
        .pg-enter-btn:active { transform: scale(0.985); }

        @media (max-width: 640px) {
          .pg-logo-img    { height: 48px !important; }
          .pg-logo-text   { font-size: 24px !important; }
          .pg-shell       { padding-top: 28px !important; padding-bottom: 40px !important; padding-left: 16px !important; padding-right: 16px !important; }
          .pg-carousel    { margin-bottom: 24px !important; }
        }
      `}</style>

      {/* ── Full-page shell ── */}
      <div className="pg-shell" style={{
        minHeight: '100vh',
        background: L.bgPage,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: isMobile ? '28px 16px 40px' : '56px 20px 80px',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif',
        boxSizing: 'border-box',
      }}>

        {/* ── Logo ── */}
        <div className="pg-item-1" style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 44,
        }}>
          <img
            src="/Darwin_v3_vinyl_transparent.png"
            alt=""
            className="pg-logo-img"
            style={{ height: isMobile ? 48 : 80, width: 'auto', objectFit: 'contain', flexShrink: 0 }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
            <span className="pg-logo-text" style={{
              fontSize: isMobile ? 24 : 36,
              fontWeight: 700,
              color: L.textPrimary,
              letterSpacing: '-0.035em',
            }}>
              Primate
            </span>
            <span className="pg-logo-text" style={{
              fontSize: isMobile ? 24 : 36,
              fontWeight: 700,
              color: L.signal,
              letterSpacing: '-0.035em',
            }}>
              Intelligence
            </span>
          </div>
        </div>

        {/* ── Carousel ── */}
        <div className="pg-item-2 pg-carousel" style={{
          width: '100%',
          maxWidth: 860,
          marginBottom: isMobile ? 24 : 36,
        }}>
          <BeforeStateCarousel />
        </div>

        {/* ── Password card ── */}
        <div className="pg-item-3" style={{ width: '100%', maxWidth: 400 }}>

          {/* Divider with label */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 20,
          }}>
            <div style={{ flex: 1, height: 1, background: L.border }} />
            <span style={{
              fontSize: 10,
              fontFamily: '"JetBrains Mono", monospace',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: L.textMuted,
              whiteSpace: 'nowrap',
            }}>
              Dev Preview
            </span>
            <div style={{ flex: 1, height: 1, background: L.border }} />
          </div>

          {/* Card */}
          <div style={{
            background: L.bgSurface,
            border: `1px solid ${L.border}`,
            borderRadius: 16,
            padding: '28px 28px 24px',
            boxShadow:
              '0 1px 2px rgba(0,0,0,0.04),' +
              '0 4px 12px rgba(0,0,0,0.06),' +
              '0 12px 32px rgba(0,0,0,0.04)',
          }}>
            <p style={{
              fontSize: 14,
              color: L.textMuted,
              marginBottom: 18,
              textAlign: 'center',
              lineHeight: 1.5,
            }}>
              Enter password to continue
            </p>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                type="password"
                autoFocus
                value={input}
                onChange={e => setInput(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder="Password"
                className={shake ? 'pg-shake' : ''}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: 10,
                  border: `1.5px solid ${shake ? '#EF4444' : focused ? L.signal : L.border}`,
                  background: '#FFFFFF',
                  padding: '12px 16px',
                  fontSize: 15,
                  color: L.textPrimary,
                  textAlign: 'center',
                  letterSpacing: '0.18em',
                  outline: 'none',
                  transition: 'border-color 150ms',
                  fontFamily: 'Inter, -apple-system, sans-serif',
                }}
              />
              <button
                type="submit"
                className="pg-enter-btn"
                style={{
                  width: '100%',
                  borderRadius: 10,
                  background: L.signal,
                  border: 'none',
                  padding: '13px 24px',
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  letterSpacing: '-0.01em',
                  transition: 'background 150ms, transform 80ms',
                  fontFamily: 'Inter, -apple-system, sans-serif',
                }}
              >
                Enter
              </button>
            </form>
          </div>
        </div>

      </div>
    </>
  );
}
